// Date picker. Drop-in replacement for components/DatePicker.
// Props: label, value (Date|null), onChange(date), register, error, disabled, disablePast,
// disableFuture, minDate (default 01-01-2025; pass false for none), maxDate, views, optional,
// errorMessage (shown when `error` is boolean true), selected (alias of value, react-datepicker style),
// bgcolor. views without "day" (e.g. ["month", "year"]) show a month grid and return the 1st of the month.
import { Popover } from "radix-ui";
import { CalendarDays, ChevronLeft, ChevronRight, X } from "lucide-react";
import { useState } from "react";
import { DayPicker } from "react-day-picker";
import { endOfMonth, format, isValid, startOfDay, startOfMonth } from "date-fns";
import { cn } from "./cn";
import { FieldError, Label } from "./Field";

const FIXED_MIN = new Date(2025, 0, 1);
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Month grid for views={["month"]} / ["month", "year"]. */
function MonthGrid({ value, onPick, isDisabled }) {
  const [year, setYear] = useState((value || new Date()).getFullYear());
  const nav = "grid size-8 place-items-center rounded-md text-muted hover:bg-black/[0.045] hover:text-fg";
  return (
    <div className="w-[264px] p-3">
      <div className="mb-2 flex h-8 items-center justify-between">
        <button type="button" className={nav} onClick={() => setYear((y) => y - 1)} aria-label="Previous year">
          <ChevronLeft className="size-4" />
        </button>
        <span className="text-[13.5px] font-semibold text-fg">{year}</span>
        <button type="button" className={nav} onClick={() => setYear((y) => y + 1)} aria-label="Next year">
          <ChevronRight className="size-4" />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-1">
        {MONTHS.map((m, i) => {
          const d = new Date(year, i, 1);
          const sel = value && value.getFullYear() === year && value.getMonth() === i;
          const dis = isDisabled(d);
          return (
            <button
              key={m}
              type="button"
              disabled={dis}
              onClick={() => onPick(d)}
              className={cn("h-9 rounded-md text-[13px] hover:bg-black/[0.045] disabled:pointer-events-none disabled:text-faint/40", sel && "bg-accent text-white hover:bg-accent")}
            >
              {m}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const dayPickerClasses = {
  root: "p-3 text-[13px] text-text",
  months: "relative",
  month_caption: "flex h-8 items-center justify-center font-semibold text-fg",
  caption_label: "text-[13.5px]",
  nav: "absolute inset-x-0 top-0 flex h-8 items-center justify-between",
  button_previous: "grid size-8 place-items-center rounded-md text-muted hover:bg-black/[0.045] hover:text-fg disabled:opacity-30",
  button_next: "grid size-8 place-items-center rounded-md text-muted hover:bg-black/[0.045] hover:text-fg disabled:opacity-30",
  chevron: "size-4 fill-current",
  month_grid: "mt-2 border-collapse",
  weekdays: "",
  weekday: "h-8 w-9 text-[11.5px] font-medium text-faint",
  week: "",
  day: "p-0 text-center",
  day_button: "size-9 rounded-md text-[13px] hover:bg-black/[0.045] disabled:pointer-events-none",
  today: "font-semibold text-accent",
  selected: "[&>button]:bg-accent [&>button]:text-white [&>button]:hover:bg-accent",
  outside: "text-faint/60",
  disabled: "text-faint/40",
  dropdowns: "flex items-center gap-1",
  dropdown: "rounded-md border border-line bg-panel px-1.5 py-1 text-[13px]",
};

export function DatePicker({
  label,
  value,
  onChange,
  register,
  error,
  disabled,
  disablePast,
  disableFuture,
  minDate,
  maxDate,
  views,
  optional,
  errorMessage,
  selected,
  bgcolor,
  className,
}) {
  const [open, setOpen] = useState(false);
  const current = value ?? selected;
  const date = current ? new Date(current) : null;
  const valid = date && isValid(date);
  const min = minDate === false ? undefined : minDate || FIXED_MIN;
  const today = startOfDay(new Date());

  const disabledDays = [
    ...(min ? [{ before: new Date(min) }] : []),
    ...(maxDate ? [{ after: new Date(maxDate) }] : []),
    ...(disablePast ? [{ before: today }] : []),
    ...(disableFuture ? [{ after: today }] : []),
  ];

  const emit = (d) => {
    onChange?.(d ?? null);
    if (register?.onChange && register?.name) {
      register.onChange({ target: { name: register.name, value: d ?? null }, type: "change" });
    }
  };

  const monthOnly = Array.isArray(views) && !views.includes("day");
  const display = valid ? format(date, monthOnly ? "MMM yyyy" : "dd-MM-yyyy") : "";

  return (
    <div className={cn("mb-4", className)}>
      {label && (
        <Label optional={optional} error={Boolean(error)}>
          {label}
        </Label>
      )}
      <Popover.Root open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
        <Popover.Trigger asChild disabled={disabled}>
          <button
            type="button"
            name={register?.name}
            ref={register?.ref}
            style={bgcolor ? { backgroundColor: bgcolor } : undefined}
            onBlur={register?.onBlur}
            className={cn(
              "flex h-10 w-full items-center gap-2 rounded-lg border border-line bg-panel px-3 text-left text-[13.5px] shadow-card outline-none transition-[border-color,box-shadow] hover:border-line-strong focus-visible:border-accent focus-visible:ring-3 focus-visible:ring-accent/20 disabled:cursor-not-allowed disabled:bg-[#f4f4f6]",
              open && "border-accent ring-3 ring-accent/20",
              error && "border-danger",
            )}
          >
            <CalendarDays className="size-4 shrink-0 text-faint" />
            <span className={cn("flex-1 truncate", display ? "text-fg" : "text-faint")}>{display || (monthOnly ? "MMM YYYY" : "DD-MM-YYYY")}</span>
            {valid && !disabled && (
              <X
                className="size-4 shrink-0 text-faint hover:text-fg"
                aria-label="Clear date"
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  emit(null);
                }}
              />
            )}
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content align="start" sideOffset={4} className="z-[1400] rounded-xl border border-line bg-panel shadow-pop">
            {monthOnly ? (
              <MonthGrid
                value={valid ? date : null}
                isDisabled={(d) =>
                  (min && endOfMonth(d) < new Date(min)) ||
                  (maxDate && startOfMonth(d) > new Date(maxDate)) ||
                  (disablePast && endOfMonth(d) < today) ||
                  (disableFuture && startOfMonth(d) > today)
                }
                onPick={(d) => {
                  emit(d);
                  setOpen(false);
                }}
              />
            ) : (
            <DayPicker
              mode="single"
              selected={valid ? date : undefined}
              defaultMonth={valid ? date : undefined}
              onSelect={(d) => {
                emit(d);
                setOpen(false);
              }}
              disabled={disabledDays}
              startMonth={min ? new Date(min) : new Date(2015, 0)}
              endMonth={maxDate ? new Date(maxDate) : new Date(today.getFullYear() + 5, 11)}
              showOutsideDays
              classNames={dayPickerClasses}
            />
            )}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      <FieldError>{typeof error === "string" ? error : error ? errorMessage : null}</FieldError>
    </div>
  );
}

export default DatePicker;
